import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { Eye } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { readViewAs } from '@/features/auth/view-as';
import { useExitViewAs } from '@/features/auth/view-as.hooks';

/** `setTimeout` fires at once on anything longer (a 32-bit delay). */
const MAX_TIMER_MS = 2 ** 31 - 1;

/**
 * Stays on top of every CMS page while a super Admin views as another role,
 * with the way out. Read once: starting and ending a view-as both load a new
 * document, so the session cannot change under a mounted banner.
 */
function ViewAsBanner() {
  const [viewAs] = useState(readViewAs);
  const { exit, isExiting } = useExitViewAs();

  // The token dies on the server at `expiresAt`; hand the CMS back to the
  // Admin then rather than waiting for the next request to bounce.
  useEffect(() => {
    if (!viewAs) return;

    const left = new Date(viewAs.session.expiresAt).getTime() - Date.now();
    const timer = window.setTimeout(
      () => void exit({ endRemote: false }),
      Math.min(Math.max(left, 0), MAX_TIMER_MS)
    );

    return () => window.clearTimeout(timer);
  }, [viewAs, exit]);

  if (!viewAs) return null;

  const { roleName, companyName, expiresAt } = viewAs.session;

  return (
    <div
      role="status"
      className="bg-primary-10 border-primary-30 w-full border-b"
    >
      <div className="flex items-center gap-3 px-6 py-3">
        <Eye className="text-base-orange-40 size-4 flex-none" />

        <p className="text-text-secondary flex-1 text-sm">
          Viewing as <span className="font-bold">{roleName}</span> of{' '}
          <span className="font-bold">{companyName}</span>. Read-only, ends at{' '}
          {format(new Date(expiresAt), 'HH:mm')}.
        </p>

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => void exit()}
          disabled={isExiting}
        >
          Exit
        </Button>
      </div>
    </div>
  );
}

export { ViewAsBanner };
